/**
 * Interest + mutual match domain (UI-agnostic).
 * Storage and pure logic live in interestStore / interestEngine.
 */

export type InterestStatus = "liked" | "passed";

export type MatchStatus = "active" | "unmatched";

/** One-way interest from one user to another. */
export interface Interest {
  id: string;
  fromUserId: string;
  toUserId: string;
  status: InterestStatus;
  createdAt: string;
}

/**
 * Mutual match — only created when both sides have status "liked".
 * userIds are always sorted lexicographically for a stable pair key.
 */
export interface MutualMatch {
  id: string;
  userIds: [string, string];
  createdAt: string;
  status: MatchStatus;
  /** Weighted values compatibility at match time (0–100). */
  valuesScore: number;
  /** Chat unlocked for this pair (after mutual match / explicit open). */
  chatUnlocked?: boolean;
}

/** Minimal local chat message (prototype). */
export interface ChatMessage {
  id: string;
  matchId: string;
  fromUserId: string;
  body: string;
  createdAt: string;
}

export interface ExpressInterestInput {
  fromUserId: string;
  toUserId: string;
  status: InterestStatus;
  /** Full interest list (or relevant subset). */
  existingInterests: Interest[];
  /** Existing mutual matches. */
  existingMatches: MutualMatch[];
  /** Optional compatibility score when a match is created. */
  valuesScore?: number;
  now?: Date;
}

export interface ExpressInterestResult {
  interest: Interest;
  match: MutualMatch | null;
  mutual: boolean;
}

export function pairKey(a: string, b: string): string {
  return a < b ? `${a}__${b}` : `${b}__${a}`;
}

export function sortedUserIds(a: string, b: string): [string, string] {
  return a < b ? [a, b] : [b, a];
}
